import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import {
  ErrorAlertComponent,
  FeatureHeaderComponent,
  OutlinedSubmitButtonComponent,
  SubmitButtonComponent,
  formatDateInputValue,
  generateUUID,
} from '@sandbox-platform/shared-ui';

import { CommissionInvoiceCreateStore } from './+state/commission-invoice-create.store';
import {
  buildPayeeInfo,
  CREATE_STEPS,
  CreateStep,
  InvoiceCreateParams,
  parseInvoiceCreateParams,
} from './definitions';
import { CommissionInvoicePayeeStepComponent } from './components/commission-invoice-payee-step.component';
import { CommissionInvoiceCommissionsStepComponent } from './components/commission-invoice-commissions-step.component';
import { PendingCommissionDTO } from '../../../types/commission-invoice.types';
import { COMMISSION_INVOICE_PAYEE_TYPE, CommissionInvoicePayeeType } from '../../../shared/definitons';
import { ExpertDTO } from '../../../types/expert.types';
import { OrganizationDTO } from '../../../types/organization.types';

@Component({
  standalone: true,
  imports: [
    ErrorAlertComponent,
    FeatureHeaderComponent,
    OutlinedSubmitButtonComponent,
    SubmitButtonComponent,
    CommissionInvoicePayeeStepComponent,
    CommissionInvoiceCommissionsStepComponent,
  ],
  selector: 'lib-commission-invoice-create',
  templateUrl: './commission-invoice-create.component.html',
  host: { class: 'block h-full overflow-y-auto' },
})
export class CommissionInvoiceCreateComponent implements OnInit {
  protected readonly store = inject(CommissionInvoiceCreateStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly CREATE_STEPS = CREATE_STEPS;

  protected lockedPayee = signal(false);
  protected companyPayeeName = signal<string>('');

  protected periodStart = signal<string>('');
  protected periodEnd = signal<string>('');
  protected dueDate = signal<string>('');
  protected notes = signal<string>('');

  protected readonly PAYEE_TYPE = COMMISSION_INVOICE_PAYEE_TYPE;

  protected readonly canProceed = computed(() => {
    if (this.store.payeeType() === COMMISSION_INVOICE_PAYEE_TYPE.ExpertIndividual)
      return this.store.selectedExpert() !== null;
    if (this.store.payeeType() === COMMISSION_INVOICE_PAYEE_TYPE.ExpertOrganization)
      return this.store.selectedOrg() !== null;
    return true;
  });

  protected readonly hasSelections = computed(
    () => this.store.selectedKeys().size > 0
  );

  /**
   * Returns true when the payee selection step is active
   * @returns boolean
   */
  protected isPayeeStep(): boolean {
    return this.store.isPayeeStep();
  }

  /**
   * Returns true when the commissions selection step is active
   * @returns boolean
   */
  protected isCommissionsStep(): boolean {
    return this.store.isCommissionsStep();
  }

  /**
   * Parses query params and delegates to applyParams and prefillPayee
   * @returns Promise<void>
   */
  async ngOnInit(): Promise<void> {
    const params = parseInvoiceCreateParams(this.route.snapshot.queryParamMap);
    this.applyParams(params);
    await this.store.prefillPayee(params);
  }

  /**
   * Applies scalar query param values to component signals; no API calls
   * @param params - InvoiceCreateParams
   */
  private applyParams(params: InvoiceCreateParams): void {
    if (params.periodStart)
      this.periodStart.set(formatDateInputValue(params.periodStart));
    if (params.periodEnd)
      this.periodEnd.set(formatDateInputValue(params.periodEnd));
    if (params.notes) this.notes.set(params.notes);
    if (params.commissionId) this.lockedPayee.set(true);
    if (params.payeeName) this.companyPayeeName.set(params.payeeName);
  }

  /**
   * Handles payee type radio change; delegates to store which clears selections
   * @param type - CommissionInvoicePayeeType
   */
  onPayeeTypeChange(type: CommissionInvoicePayeeType): void {
    this.store.setPayeeType(type);
  }

  /**
   * Sets the selected expert when one is chosen from the expert selection input
   * @param expert - ExpertDTO
   */
  onExpertSelected(expert: ExpertDTO): void {
    this.store.setSelectedExpert(expert);
  }

  /**
   * Clears the selected expert when the selection is cleared
   */
  onExpertCleared(): void {
    this.store.setSelectedExpert(null);
  }

  /**
   * Sets the selected organization when one is chosen from the org selection input
   * @param org - OrganizationDTO
   */
  onOrgSelected(org: OrganizationDTO): void {
    this.store.setSelectedOrg(org);
  }

  /**
   * Clears the selected organization when the selection is cleared
   */
  onOrgCleared(): void {
    this.store.setSelectedOrg(null);
  }

  /**
   * Builds payee info from store selections and advances to the commissions step
   * @returns Promise<void>
   */
  async onNext(): Promise<void> {
    const payeeInfo = buildPayeeInfo(
      this.store.payeeType(),
      this.store.selectedExpert(),
      this.store.selectedOrg(),
      this.companyPayeeName()
    );
    if (!payeeInfo) return;
    await this.store.loadPendingCommissions(payeeInfo);
  }

  /**
   * Navigates back to the commissions list
   */
  protected goBack(): void {
    this.router.navigate(['/commissions']);
  }

  /**
   * Handles the back button; returns to the payee step if on commissions,
   * or navigates away to the commissions list if on the payee step
   */
  onBack(): void {
    if (this.store.isCommissionsStep()) {
      this.store.goBack();
    } else {
      this.goBack();
    }
  }

  /**
   * Navigates to the given step; advances via onNext when moving to commissions,
   * or goes back to the payee step when moving backwards
   * @param step - CreateStep
   */
  goToStep(step: CreateStep): void {
    if (this.store.isPayeeStep()) {
      this.onNext();
    } else {
      this.store.goToPayee();
    }
  }

  /**
   * Toggles selection of a pending commission on the commissions step
   * @param commission - PendingCommissionDTO
   */
  toggleCommission(commission: PendingCommissionDTO): void {
    this.store.toggleSelection(commission);
  }

  /**
   * Submits the invoice using selected commissions and optional period/notes fields;
   * resets the store and navigates to the commissions list on success
   * @returns Promise<void>
   */
  async onSubmit(): Promise<void> {
    const currency = this.store.selectedCommissions()[0]?.currency ?? 'KES';

    const invoice = await this.store.submit(
      generateUUID(),
      generateUUID(),
      currency,
      this.periodStart() || undefined,
      this.periodEnd() || undefined,
      this.dueDate() || undefined,
      this.notes() || undefined
    );

    if (invoice) {
      this.store.reset();
      this.router.navigate(['/commissions']);
    }
  }
}
